import type * as React from 'react'
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { Cell, CellProps, HeaderGroup, useTable } from 'react-table'
import {
  faChevronRight,
  faCircleChevronDown,
} from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'
import { isEmpty } from 'lodash-es'

import { Heading } from 'Components/_theme'
import { Icon } from 'Components/Icon/Icon'
import { NoResults } from 'Components/NoResults/NoResults'
import { SearchLoadingState } from 'Components/SearchLoadingState/SearchLoadingState'

import { E2E_TABLE_ROW } from 'Constants/e2e'

import { TABLE_LIST_ROW } from './TableList.constants'
import { TableListProps, TableListStaticColumnsEnum } from './TableList.types'

export const TableList = <
  T extends {
    url?: string
  },
>(
  props: TableListProps<T>,
) => {
  const {
    className,
    columns,
    data,
    e2eRowId,
    isLoading,
    isSearching,
    hideArrow,
    selectedRowIndex,
  } = props
  const rowsRef = useRef<Map<number, HTMLTableRowElement | null>>(new Map())

  const intl = useIntl()

  const createHeaderClassNames = useCallback((column: HeaderGroup<T>) => {
    const baseClass = 'Table-cell Table-cell--head'

    if (column.id === TableListStaticColumnsEnum.DETAILS) {
      return clsx(baseClass, 'Table-cell--details')
    }

    if (column.id === TableListStaticColumnsEnum.ICON) {
      return clsx(baseClass, 'Table-cell--icon')
    }

    return baseClass
  }, [])

  const createCellClassNames = useCallback((cell: Cell<T, any>) => {
    const baseClass = 'Table-cell'

    if (cell.column.id === TableListStaticColumnsEnum.DETAILS) {
      return clsx(baseClass, 'Table-cell--details')
    }

    if (cell.column.id === TableListStaticColumnsEnum.ICON) {
      return clsx(baseClass, 'Table-cell--icon')
    }

    return baseClass
  }, [])

  const createDataE2E = useCallback(
    (cell: Cell<T, any>) => ({
      ...(cell.column.id === TableListStaticColumnsEnum.ICON && {
        'data-e2e': e2eRowId,
      }),
    }),
    [e2eRowId],
  )

  const handleExpandRow = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, clickedRowIndex: number) => {
      const { current: rows } = rowsRef

      if (!rows) {
        return
      }

      rows.forEach((row, rowIndex) => {
        if (rowIndex !== clickedRowIndex) {
          row?.classList.remove('Table-row--isExpanded')
        }
      })

      rows.get(clickedRowIndex)?.classList.toggle('Table-row--isExpanded')
    },
    [],
  )

  const renderDetailsButton = useCallback(
    (cell: React.PropsWithChildren<CellProps<T>>) => (
      <button
        aria-label={intl.formatMessage({ id: 'Expand_content' })}
        className="Table-cell--details-button"
        onClick={event => handleExpandRow(event, cell.row.index)}
        type="button"
      >
        <Icon icon={faCircleChevronDown} size={20} />
      </button>
    ),
    [handleExpandRow, intl],
  )

  const renderIcon = useCallback(
    (cell: React.PropsWithChildren<CellProps<T>>) => {
      const { url } = cell.row.original

      if (!url) {
        return null
      }

      return (
        <Link
          aria-label={intl.formatMessage({ id: 'PreviewColumn_footer_button' })}
          title={intl.formatMessage({ id: 'PreviewColumn_footer_button' })}
          to={url}
        >
          <div className="Table-chevron d-flex justify-content-end">
            <Icon icon={faChevronRight} size={16} />
          </div>
        </Link>
      )
    },
    [intl],
  )

  const tableColumns = useMemo(() => {
    const detailsColumn = {
      Cell: renderDetailsButton,
      id: TableListStaticColumnsEnum.DETAILS,
    }

    const iconColumn = {
      Cell: renderIcon,
      id: TableListStaticColumnsEnum.ICON,
    }

    const preparedColumns = columns.map(column => ({
      ...column,
    }))

    if (hideArrow) {
      return [detailsColumn, ...preparedColumns]
    }

    return [detailsColumn, ...preparedColumns, iconColumn]
  }, [columns, hideArrow, renderDetailsButton, renderIcon])

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns: tableColumns,
      data,
    })

  const renderProgressInfo = useMemo(() => {
    let progressInfo = null

    if (isSearching || isLoading) {
      progressInfo = <SearchLoadingState />
    }

    if (!isSearching && isEmpty(data)) {
      progressInfo = <NoResults className="pb-4" />
    }

    if (!progressInfo) {
      return null
    }

    return (
      <tr className="Table-row Table-row--no-results" role="row">
        <td className="Table-cell" role="cell">
          {progressInfo}
        </td>
      </tr>
    )
  }, [isSearching, isLoading, data])

  const renderRows = useMemo(
    () =>
      rows.map(row => {
        prepareRow(row)

        const expandableContentIndex = row.cells.findIndex(
          cell => cell.column.ExpandableContent,
        )

        return (
          <Fragment key={`row__${row.index}`}>
            <tr
              ref={tableRow => {
                rowsRef.current.set(row.index, tableRow)
              }}
              className="Table-row"
              data-e2e={E2E_TABLE_ROW}
              data-testid={TABLE_LIST_ROW}
              {...row.getRowProps()}
            >
              {row.cells.map(cell => (
                <td
                  {...createDataE2E(cell)}
                  {...cell.getCellProps({
                    className: createCellClassNames(cell),
                  })}
                >
                  {cell.render('Cell')}
                </td>
              ))}
            </tr>

            <tr
              key={`row_${row.index}_expandable`}
              className="Table-row Table-row--expandable"
              role="row"
            >
              <td className="Table-row--expandable-cell w-100" role="cell">
                {expandableContentIndex &&
                  row.cells[expandableContentIndex].render('ExpandableContent')}
              </td>
            </tr>
          </Fragment>
        )
      }),
    [createCellClassNames, createDataE2E, prepareRow, rows],
  )

  useEffect(() => {
    const { current: rows } = rowsRef

    if (typeof selectedRowIndex === 'undefined') {
      return
    }

    rows.forEach(row => {
      row?.classList.remove('Table-row--isSelected')
    })

    if (selectedRowIndex !== null && rows.has(selectedRowIndex)) {
      rows.get(selectedRowIndex)?.classList.add('Table-row--isSelected')
    }
  }, [selectedRowIndex])

  return (
    <table className={clsx('Table', className)} {...getTableProps()}>
      <thead className="Table-head">
        {headerGroups.map(headerGroup => (
          <tr
            className="Table-row Table-row--head"
            {...headerGroup.getHeaderGroupProps()}
          >
            {headerGroup.headers.map(column => (
              <th
                {...column.getHeaderProps({
                  className: createHeaderClassNames(column),
                })}
              >
                <Heading as={6} size={2}>
                  {column.render('Header')}
                </Heading>
              </th>
            ))}
          </tr>
        ))}
      </thead>

      <tbody className="Table-body" {...getTableBodyProps()}>
        {renderRows}
        {renderProgressInfo}
      </tbody>
    </table>
  )
}
